import { styled } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Head } from '@unhead/react';
import { UnheadProvider, createHead } from '@unhead/react/client';
import React, { Suspense, useMemo } from 'react';
import { NetworkStatus, createNetworkStatusNotifier } from 'react-apollo-network-status';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Navigate, Routes as ReactRouterRoutes, Route, useMatch } from 'react-router';
import { Provider as APIProvider } from 'src/api/provider';
import {
    DEFAULT_SEGMENT_WRITE_KEY,
    OPS_SEGMENT_WRITE_KEY,
    ORC_SEGMENT_WRITE_KEY,
    PMC_SEGMENT_WRITE_KEY,
    RMC_SEGMENT_WRITE_KEY,
} from 'src/constants/environment';
import { FONT_URL } from 'src/constants/ui';
import { AccountType } from 'src/graphql/client';
import { SessionDetailsFragment } from 'src/graphql/lib';
import { GraphqlProvider } from 'src/helpers/graphql';
import { AnalyticsProvider } from 'src/lib/analytics';
import { AnalyticsContextProvider } from 'src/lib/analytics/context';
import {
    AnalyticsAccountType,
    AnalyticsEventSource,
    AnalyticsProductWorkflow,
    AnalyticsSource,
} from 'src/lib/analytics/events';
import { ThemeProvider } from 'src/lib/style';
import { baseTheme } from 'src/theme';
import { AuthThemeProvider } from './Auth/AuthThemeProvider';
import { FacilityAppSSO } from './Auth/FacilityAppSSO';
import { MagicLink } from './Auth/MagicLink';
import SingleSignOn from './Auth/SingleSignOn';
import ForgotPassword from './ForgotPassword';
import Impersonate from './Impersonate';
import QRCodeLanding from './QRCodeLanding';
import { ResQRouter } from './ResQRouter';
import ResetPassword from './ResetPassword';
import { SignOut } from './SignOut';
import VendorLinkInvalid from './VendorLinkInvalid';
import { BannerProvider } from './bannerContext';
import Loader from './components/atoms/Loader';
import SentryUserContext from './components/domain/SentryUserContext';
import { ConfirmLeaveProvider } from './confirmLeaveContext';
import { FeatureFlagsProvider } from './featureFlagsContext';
import { lazyComponentLoader } from './helpers';
import { LocaleProvider } from './localeContext';
import { NavBarProvider } from './navBarContext';
import { SessionProvider, useNullableSessionUser } from './sessionContext';
import { SnackbarProvider } from './snackbar';

const CoordinatorDashboard = lazyComponentLoader(() => import('./CoordinatorDashboard'));
const ClientDashboard = lazyComponentLoader(() => import('./ClientDashboard'));
const VendorDashboard = lazyComponentLoader(() => import('./VendorDashboard'));
const MissingOrganizationPage = lazyComponentLoader(() => import('./MissingOrganizationPage'));
const Auth = lazyComponentLoader(() => import('./Auth'));
const OffResqCustomerPortal = lazyComponentLoader(() => import('./OffResqCustomer'));
const PublicBusinessProfile = lazyComponentLoader(
    () => import('../lib/vendor/PublicBusinessProfile'),
);

interface PlatformResult {
    platform: React.LazyExoticComponent<React.ComponentType<any>> | null;
    analyticsSource: AnalyticsSource;
    productWorkflow: AnalyticsProductWorkflow | null;
    writeKey: string;
    accountType: AnalyticsAccountType | undefined;
}

const usePlatform = (me: SessionDetailsFragment | null): PlatformResult => {
    const offResQCustomerMatch = useMatch('/fast-pay-customer/*');
    const acceptInviteMatch = useMatch('/accept-invite/*');
    const declineInviteMatch = useMatch('/decline-invite/*');
    const inviteAlreadyAcceptedMatch = useMatch('/invite-already-accepted/*');
    const exceptionRouteMatch =
        acceptInviteMatch || declineInviteMatch || inviteAlreadyAcceptedMatch;

    return useMemo((): PlatformResult => {
        if (offResQCustomerMatch) {
            return {
                platform: OffResqCustomerPortal,
                analyticsSource: AnalyticsSource.OFF_RESQ_CUSTOMER_PORTAL,
                productWorkflow: null,
                writeKey: ORC_SEGMENT_WRITE_KEY,
                accountType: AnalyticsAccountType.OFF_RESQ_CUSTOMER_PORTAL,
            };
        }

        if (me && !exceptionRouteMatch) {
            if ([AccountType.Coordinator, AccountType.ControlCenter].includes(me.accountType)) {
                return {
                    platform: CoordinatorDashboard,
                    analyticsSource: AnalyticsSource.RCC,
                    productWorkflow: null,
                    writeKey: OPS_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.RCC,
                };
            }

            if (me.accountType === AccountType.Client) {
                return {
                    platform: ClientDashboard,
                    analyticsSource: AnalyticsSource.RMC,
                    productWorkflow: null,
                    writeKey: RMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.RMC,
                };
            }

            if (me.accountType === AccountType.Vendor) {
                return {
                    platform: VendorDashboard,
                    analyticsSource: AnalyticsSource.PMC,
                    productWorkflow: null,
                    writeKey: PMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.PMC,
                };
            }

            if (me.accountType === AccountType.OffPlatform) {
                return {
                    platform: OffResqCustomerPortal,
                    analyticsSource: AnalyticsSource.OFF_RESQ_CUSTOMER_PORTAL,
                    productWorkflow: null,
                    writeKey: PMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.OFF_RESQ_CUSTOMER_PORTAL,
                };
            }

            if (me.accountType === AccountType.Independent) {
                return {
                    platform: MissingOrganizationPage,
                    analyticsSource: AnalyticsSource.INDEPENDENT,
                    productWorkflow: null,
                    writeKey: PMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.INDEPENDENT,
                };
            }
        }

        return {
            platform: Auth,
            analyticsSource: AnalyticsSource.LOGIN,
            productWorkflow: AnalyticsProductWorkflow.Login,
            writeKey: DEFAULT_SEGMENT_WRITE_KEY,
            accountType: undefined,
        };
    }, [me, offResQCustomerMatch, exceptionRouteMatch]);
};

const Routes = React.memo(() => {
    const me = useNullableSessionUser();

    const { platform, analyticsSource, productWorkflow, writeKey, accountType } = usePlatform(me);

    return (
        <AnalyticsContextProvider
            value={{
                application: analyticsSource,
                eventSource: AnalyticsEventSource.FRONTEND,
                productWorkflow,
            }}
        >
            <AnalyticsProvider segmentWriteKey={writeKey} accountType={accountType}>
                <ReactRouterRoutes>
                    <Route path="/sign-out" element={<SignOut />} />
                    <Route path="/impersonate/:userId" element={<Impersonate />} />
                    <Route path="/qrcode" element={<QRCodeLanding />} />
                    <Route path="/invalid-link" element={<VendorLinkInvalid />} />
                    <Route
                        path="/reset-password/:userId/:token"
                        element={
                            <AuthThemeProvider>
                                <ResetPassword />
                            </AuthThemeProvider>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <AuthThemeProvider>
                                <ForgotPassword />
                            </AuthThemeProvider>
                        }
                    />
                    <Route
                        path="/password-free-link"
                        element={
                            <AuthThemeProvider>
                                <MagicLink />
                            </AuthThemeProvider>
                        }
                    />
                    <Route
                        path="/facility-app-sso"
                        element={
                            <AuthThemeProvider>
                                <FacilityAppSSO />
                            </AuthThemeProvider>
                        }
                    />
                    <Route
                        path="/sso"
                        element={
                            <AuthThemeProvider>
                                <SingleSignOn />
                            </AuthThemeProvider>
                        }
                    />
                    <Route path="/business/:id" element={<PublicBusinessProfile />} />
                    {platform ? (
                        <>
                            <Route path="/" element={React.createElement(platform)} />
                            <Route path="*" element={React.createElement(platform)} />
                        </>
                    ) : (
                        <>
                            <Route path="/" element={<Navigate to="/sign-out" replace />} />
                            <Route path="*" element={<Navigate to="/sign-out" replace />} />
                        </>
                    )}
                </ReactRouterRoutes>
            </AnalyticsProvider>
        </AnalyticsContextProvider>
    );
});

const Wrapper = styled('div')``;

type Wrap = (children?: React.ReactNode) => React.ReactElement<any, any> | null;

const wrap = (layers: Wrap[]) => {
    return layers
        .reverse()
        .reduce(
            (children, layerWrap) => layerWrap(children),
            null as React.ReactElement<any, any> | null,
        );
};

const LoaderComponent = ({ apolloNetworkStatus }: { apolloNetworkStatus: NetworkStatus }) => {
    return apolloNetworkStatus.numPendingQueries > 0 ? <Loader /> : null;
};

const queryClient = new QueryClient();

export const App = () => {
    const { link: networkStatusNotifierLink, useApolloNetworkStatus } = useMemo(
        () => createNetworkStatusNotifier(),
        [],
    );

    const apolloNetworkStatus = useApolloNetworkStatus();

    const head = useMemo(
        () =>
            createHead({
                // Disable DOM updates in test environment to prevent conflicts
                document:
                    process.env.NODE_ENV === 'test' || process.env.TESTING === 'true'
                        ? undefined
                        : typeof window !== 'undefined'
                        ? window.document
                        : undefined,
            }),
        [],
    );

    const Wrapped = useMemo(
        () =>
            wrap([
                (children) => <ResQRouter>{children}</ResQRouter>,
                (children) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        {children}
                    </LocalizationProvider>
                ),
                (children) => <ThemeProvider theme={baseTheme}>{children}</ThemeProvider>,
                (children) => <SnackbarProvider>{children}</SnackbarProvider>,
                (children) => <APIProvider>{children}</APIProvider>,
                (children) => (
                    <GraphqlProvider networkStatusNotifierLink={networkStatusNotifierLink}>
                        {children}
                    </GraphqlProvider>
                ),
                (children) => (
                    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
                ),
                (children) => <SessionProvider>{children}</SessionProvider>,
                (children) => <FeatureFlagsProvider>{children}</FeatureFlagsProvider>,
                (children) => <LocaleProvider>{children}</LocaleProvider>,
                (children) => <BannerProvider>{children}</BannerProvider>,
                (children) => <NavBarProvider>{children}</NavBarProvider>,

                () => (
                    <ConfirmLeaveProvider>
                        <SentryUserContext />
                        <Suspense fallback={null}>
                            <Wrapper>
                                <Routes />
                            </Wrapper>
                        </Suspense>
                    </ConfirmLeaveProvider>
                ),
            ]),
        [networkStatusNotifierLink],
    );

    return (
        <UnheadProvider head={head}>
            <Head>
                <link rel="stylesheet" href={FONT_URL} />
            </Head>
            <LoaderComponent apolloNetworkStatus={apolloNetworkStatus} />
            {Wrapped}
        </UnheadProvider>
    );
};
